import { Button, Loader, Rating, Select, Textarea } from '@mantine/core';
import { IconStar, IconStarFilled } from '@tabler/icons-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import classes from './SupplierProfile.module.css';
import type {
  SupplierProfileSource,
  SupplierReviewStatus,
  SupplierReviewsView,
} from './supplier-profile.js';

const STATUS_TONE: Record<SupplierReviewStatus, StatusTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  withdrawn: 'neutral',
};

/** Puan yıldızları (salt görüntü); ekran okuyucuya "5 üzerinden 4". */
function Stars({ rating, label }: { rating: number; label: string }) {
  return (
    <span role="img" aria-label={label} className={classes.stars}>
      {[1, 2, 3, 4, 5].map((n) =>
        n <= rating ? (
          <IconStarFilled key={n} size={14} aria-hidden data-filled />
        ) : (
          <IconStar key={n} size={14} aria-hidden />
        ),
      )}
    </span>
  );
}

/**
 * Tedarikçi profil penceresinin "Reviews" sekmesi (ADR 0120 §4): platformun yayımladığı partner
 * yorumları (yazan şirket gizli, tarih ay düzeyinde), şirketin kendi yorumlarının durumu ve
 * teslim alınmış siparişe yorum yazma (admin onayından sonra yayımlanır).
 */
export function SupplierReviewsTab({
  supplierId,
  supplierName,
  source,
  onNotice,
}: {
  supplierId: string;
  supplierName: string;
  source: SupplierProfileSource;
  onNotice?: (message: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`supplierProfile.reviews.${key}`, options);
  const [view, setView] = useState<SupplierReviewsView | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [order, setOrder] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!source.reviews) return;
    try {
      setView(await source.reviews(supplierId));
    } catch (caught) {
      setFailed(errorCode(caught));
    }
  }, [source, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);

  const month = (value: string) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${value}-01T00:00:00Z`));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!source.writeReview || !order) return;
    if (rating < 1) {
      setError('INVALID_RATING');
      return;
    }
    if (comment.trim().length < 10) {
      setError('TOO_SHORT');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await source.writeReview(supplierId, {
        purchaseOrderId: order,
        rating,
        comment: comment.trim(),
      });
      setView(next);
      setWriting(false);
      setOrder(null);
      setRating(0);
      setComment('');
      onNotice?.(k('submitted'));
    } catch (caught) {
      setError(errorCode(caught));
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return (
      <p className={classes.lead} role="alert">
        {t(`purchasing.errors.${failed}`, { defaultValue: t('purchasing.errors.GENERIC') })}
      </p>
    );
  }
  if (!view) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: 120 }}>
        <Loader aria-label={t('supplierProfile.loading')} />
      </div>
    );
  }

  const fieldError = (code: string | null) =>
    code
      ? t(`purchasing.errors.${code}`, {
          defaultValue: t(`supplierProfile.reviewErrors.${code}`, {
            defaultValue: t('purchasing.errors.GENERIC'),
          }),
        })
      : null;

  return (
    <div className={classes.reviews}>
      <div className={classes.reviewSummary}>
        {view.summary?.average != null && view.summary.count > 0 ? (
          <>
            <Stars
              rating={Math.round(view.summary.average)}
              label={k('rating', { rating: view.summary.average.toFixed(1) })}
            />
            <strong>
              {k('summary', {
                average: view.summary.average.toFixed(1),
                count: view.summary.count,
              })}
            </strong>
          </>
        ) : (
          <strong>{k('summaryNone')}</strong>
        )}
      </div>
      <p className={classes.lead}>{k('hint')}</p>

      {view.items.length === 0 ? null : (
        <ul className={classes.reviewList}>
          {view.items.map((item, index) => (
            <li
              // Yorum kimliği yanıtta yok (yazan gizli): sıra anahtardır
              // biome-ignore lint/suspicious/noArrayIndexKey: kimliksiz, değişmeyen liste
              key={index}
              className={classes.review}
            >
              <div className={classes.reviewHead}>
                <Stars rating={item.rating} label={k('rating', { rating: item.rating })} />
                <StatusBadge tone="info">{k('verifiedOrder')}</StatusBadge>
                {item.own ? <StatusBadge tone="neutral">{k('yourCompany')}</StatusBadge> : null}
                <span className={classes.reviewDate}>{month(item.month)}</span>
              </div>
              <p className={classes.reviewText}>{item.comment}</p>
            </li>
          ))}
        </ul>
      )}

      {view.mine.length > 0 ? (
        <>
          <h3 className={classes.sectionTitle}>{k('mineTitle')}</h3>
          <ul className={classes.reviewList}>
            {view.mine.map((item) => (
              <li key={item.id} className={classes.review}>
                <div className={classes.reviewHead}>
                  <Stars rating={item.rating} label={k('rating', { rating: item.rating })} />
                  <StatusBadge tone={STATUS_TONE[item.status]}>
                    {k(`status.${item.status}`)}
                  </StatusBadge>
                  <span className={classes.reviewDate}>
                    {[item.poNo, formatDateTime(item.createdAt)].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <p className={classes.reviewText}>{item.comment}</p>
                {item.reason ? (
                  <p className={classes.reviewReason}>{k('reason', { reason: item.reason })}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {view.canWrite && source.writeReview ? (
        writing ? (
          <form className={classes.reviewForm} onSubmit={(event) => void submit(event)} noValidate>
            <h3 className={classes.sectionTitle}>{k('writeTitle', { name: supplierName })}</h3>
            <p className={classes.lead}>{k('writeBody')}</p>
            <Select
              label={k('order')}
              required
              data={view.orders.map((o) => ({
                value: o.id,
                label: o.rewrite ? k('rewrite', { po: o.poNo }) : o.poNo,
              }))}
              value={order}
              onChange={setOrder}
              allowDeselect={false}
            />
            <div>
              <span className={classes.fieldLabel} id={`rating-${supplierId}`}>
                {k('ratingLabel')}
              </span>
              <Rating
                value={rating}
                onChange={setRating}
                count={5}
                size="lg"
                aria-labelledby={`rating-${supplierId}`}
                getSymbolLabel={(value) => k('rating', { rating: value })}
              />
              {error === 'INVALID_RATING' ? (
                <span className={classes.fieldError} role="alert">
                  {fieldError(error)}
                </span>
              ) : null}
            </div>
            <Textarea
              label={k('comment')}
              description={k('commentHint')}
              required
              autosize
              minRows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
              error={error === 'TOO_SHORT' ? fieldError(error) : null}
            />
            {error && error !== 'INVALID_RATING' && error !== 'TOO_SHORT' ? (
              <p className={classes.reviewReason} role="alert">
                {fieldError(error)}
              </p>
            ) : null}
            <div className={classes.reviewActions}>
              <Button variant="default" onClick={() => setWriting(false)}>
                {t('supplierProfile.close')}
              </Button>
              <Button type="submit" loading={busy} disabled={!order}>
                {k('submit')}
              </Button>
            </div>
          </form>
        ) : (
          <div className={classes.reviewActions}>
            {view.orders.length === 0 ? (
              <span className={classes.reviewDate}>{k('noOrders')}</span>
            ) : null}
            <Button
              onClick={() => {
                setWriting(true);
                setOrder(view.orders[0]?.id ?? null);
              }}
              disabled={view.orders.length === 0}
            >
              {k('write')}
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}
